import React, { useState } from 'react';
import { ShieldCheck, ArrowRight, CheckCircle2 } from 'lucide-react';
import quizContent from './content/quiz.json';

export default function App() {
  const [step, setStep] = useState(0);

  const WelcomeScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 flex items-center justify-center gap-2">
          TotalAV
        </h1>
      </div>
      
      <div className="bg-[#1e293b] rounded-xl p-6 w-full max-w-md border border-gray-700 shadow-2xl">
        <h2 className="text-2xl font-bold mb-4">{quizContent.title}</h2>
        <p className="text-gray-400 mb-8">{quizContent.subtitle}</p>
        
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="flex -space-x-2">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="w-8 h-8 rounded-full bg-green-500 border-2 border-[#1e293b] flex items-center justify-center">
                <span className="text-white text-xs font-bold">★</span>
              </div>
            ))}
          </div>
          <div className="text-left ml-2">
            <div className="font-bold text-sm">Trustpilot</div>
            <div className="text-xs text-gray-400">Excellent</div>
          </div>
        </div>

        <button 
          onClick={() => setStep(1)}
          className="w-full bg-control hover:bg-green-400 text-black font-bold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          Get Started <ArrowRight size={20} />
        </button>
      </div>
    </div>
  );

  const QuestionScreen = ({ questionIndex }: { questionIndex: number }) => {
    const question = quizContent.questions[questionIndex];
    const totalQuestions = quizContent.questions.length;
    const progressPercent = Math.round(((questionIndex + 1) / totalQuestions) * 100);

    return (
      <div className="flex flex-col min-h-screen p-6 max-w-md mx-auto w-full pt-12">
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
            TotalAV
          </h1>
          <div className="w-full bg-gray-700 h-2 rounded-full mt-4">
            <div className="bg-control h-2 rounded-full transition-all" style={{ width: `${progressPercent}%` }}></div>
          </div>
        </div>

        {question.description && (
          <p className="text-gray-400 mb-2">{question.description}</p>
        )}
        <h2 className="text-2xl font-bold mb-6">{question.question}</h2>

        <div className="space-y-4">
          {question.options.map((option: string, idx: number) => (
            <button 
              key={idx}
              onClick={() => {
                // simple skip logic for question 1 matching original if needed, or just linear
                if (questionIndex === 0 && idx !== 0) {
                  setStep(3); // skips Q2
                } else {
                  setStep(step + 1);
                }
              }}
              className="w-full bg-[#1e293b] border border-gray-700 hover:border-control text-left p-4 rounded-xl transition-colors flex items-center gap-4"
            >
              <div className="w-5 h-5 rounded-full border border-gray-500 flex-shrink-0"></div>
              <span dangerouslySetInnerHTML={{__html: option}}></span>
            </button>
          ))}
        </div>

        {question.did_you_know && (
          <div className="mt-auto pt-8">
            <div className="bg-[#1e293b] p-4 rounded-xl border border-gray-700 flex items-start gap-3">
              <ShieldCheck className="text-control flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-sm mb-1">Did you know?</h3>
                <p className="text-sm text-gray-400">{question.did_you_know}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const CaptureScreen = () => (
    <div className="flex flex-col min-h-screen p-6 max-w-md mx-auto w-full pt-12">
      <div className="mb-8 text-center">
        <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">{quizContent.final_title}</h2>
        <p className="text-gray-400">{quizContent.final_description}</p>
      </div>

      <div className="space-y-4">
        <a 
          href={quizContent.cta_url}
          className="w-full bg-control hover:bg-green-400 text-black font-bold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {quizContent.cta_text} <ArrowRight size={20} />
        </a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0b1120] text-white font-sans selection:bg-control selection:text-black">
      {step === 0 && <WelcomeScreen />}
      {step > 0 && step <= quizContent.questions.length && <QuestionScreen questionIndex={step - 1} />}
      {step > quizContent.questions.length && <CaptureScreen />}
    </div>
  );
}
